'use client';

import { Container } from '@/components/layout/container/container';
import { cn } from '@/lib/utils';
import type { SectionBgVariant } from '@/lib/types/misc';
import { Document } from '@contentful/rich-text-types';
import { motion } from 'motion/react';
import { HTMLAttributes, useState } from 'react';
import { Badge } from '../../ui/badge/badge';
import RichTextViewer from '../../ui/rich-text-viewer/rich-text-viewer';
import styles from './column-info-with-numbers.module.scss';

// UniversalCard type supports value, title, description, icon
export type UniversalCard = {
  id: string;
  title: string;
  description?: Document;
  value?: string;
  icon?: string | null;
};

type StatisticCardProps = {
  card: UniversalCard;
};

export const StatisticCard = ({ card }: StatisticCardProps) => {
  // parse out prefix/suffix and the numeric target
  const value = card.title || '';
  const numericMatch = value.match(/^([>£]?\s*)(\d+(?:,\d+)?)(\s*[A-Za-z]*)$/);
  const prefix = numericMatch?.[1] || '';
  const numericPart = numericMatch?.[2] || '0';
  const suffix = numericMatch?.[3] || '';
  const target = parseInt(numericPart.replace(/,/g, ''), 10) || 0;
  const start = Math.max(0, target - 10);

  // local state
  const [displayValue, setDisplayValue] = useState(start);
  const [hasAnimated, setHasAnimated] = useState(false);

  // animation runner
  const runCountUp = () => {
    setDisplayValue(start);
    const diff = target - start;
    const increment = diff / 10;
    let current = start;
    let step = 0;

    const iv = setInterval(() => {
      step++;
      current = Math.round(start + increment * step);
      if (step >= 10 || current >= target) {
        setDisplayValue(target);
        clearInterval(iv);
      } else {
        setDisplayValue(current);
      }
    }, 100);
  };

  return (
    <div key={card.id} className={styles.card}>
      {card.title && (
        <motion.div
          className={styles['card-value']}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          onViewportEnter={() => {
            if (!hasAnimated) {
              setHasAnimated(true);
              runCountUp();
            }
          }}
        >
          {prefix}
          {displayValue.toLocaleString()}
          {suffix}
        </motion.div>
      )}
      <div className={styles['card-label']}>
        <RichTextViewer content={card.description} />
      </div>
    </div>
  );
};

type ColumnInfoWithNumbersProps = {
  sectionId: string;
  badge?: string;
  title: string;
  subHeading?: Document;
  background: SectionBgVariant;
  cards: UniversalCard[];
  className?: string;
} & HTMLAttributes<HTMLElement>;

export const ColumnInfoWithNumbers = ({
  badge = '',
  title,
  subHeading,
  background = 'white',
  cards = [],
  className,
  sectionId,
  ...props
}: ColumnInfoWithNumbersProps) => {
  return (
    <section
      className={cn(
        styles['two-column-info'],
        styles[`two-column-info--bg-${background}`],
        className,
      )}
      id={sectionId}
      {...props}
    >
      <Container className={styles['two-column-info__container']}>
        <div className={styles['two-column-info__container__text-wrapper']}>
          {badge && <Badge text={badge} />}
          <div className={cn(styles['two-column-info__container__text-container'])}>
            <h2>{title}</h2>
            {subHeading && <RichTextViewer content={subHeading} />}
          </div>
        </div>

        {cards.length > 0 && (
          <>
            <motion.div
              className={styles['cards-grid--statistic']}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.04, 0.62, 0.23, 0.98] }}
              viewport={{ once: true, amount: 0.3 }}
            >
              {cards.map((card, idx) => (
                <StatisticCard key={`${card.id}-${idx}`} card={card} />
              ))}
            </motion.div>
          </>
        )}
      </Container>
    </section>
  );
};
